import get from 'lodash.get';
import { graphql, fetchQuery } from 'react-relay';

// helpers
import { wasPdpVisitedMultipleTimes } from '../pdpSharedNotificationHelpers';
import { getItemIdFromUri } from '../../../utils/getItemIdFormUri';
import { getBuyerId, getUserType } from 'dibs-cookie-jar';
import {
    hasRecentTransaction,
    isAtWeeklyViewCap,
    isAtMonthlyViewCap,
    getCurrentMicroGoal,
} from './pdpSharedNotificationTradeMicroGoalHelpers';
import {
    PRIORITIES,
    SHOW_PDP_TRADE_NEGOTIATION_TOAST,
    SHOW_PDP_TRADE_NEGOTIATION_MODAL,
    SHOW_PDP_TRADE_ON_APPROVAL_CALLOUT,
    SHOW_PDP_TRADE_HOLD_REQUEST_TOAST,
} from '../pdpSharedNotificationConstants';

const query = graphql`
    query pdpSharedNotificationTradeMicroGoalConfigQuery(
        $itemId: String = ""
        $userId: String = ""
        $buyerId: [String] = []
    ) {
        viewer {
            transactions(buyerId: $buyerId, first: 1, sort: "checkoutDate desc") {
                edges {
                    node {
                        checkoutDate
                    }
                }
            }
            conversationsSolr(buyerId: $userId, itemId: $itemId) {
                totalResults
            }
            item(itemId: $itemId, pageType: PDP) {
                canBuyerMakeOffer(buyerId: $userId)
                displayInfo(page: pdp, buyerId: $userId) {
                    ctas {
                        showRequestOnApproval
                        showHoldRequest
                    }
                }
            }
        }
    }
`;

const { PDP_TRADE_MICRO_GOALS: PDP_TRADE_MICRO_GOALS_PRIORITY } = PRIORITIES;

export const pdpSharedNotificationTradeMicroGoalConfig = {
    name: 'pdpTradeMicroGoal',
    priority: PDP_TRADE_MICRO_GOALS_PRIORITY,

    rule: ({ relayEnvironment }) => {
        const userId = getBuyerId(document.cookie);
        const isTradeUser = getUserType(document.cookie) === 'trade';
        const itemId = getItemIdFromUri(location.pathname);
        const isMultipleVisit = wasPdpVisitedMultipleTimes({ itemId });

        if (
            !itemId ||
            !userId ||
            !isTradeUser ||
            !isMultipleVisit ||
            isAtWeeklyViewCap() ||
            isAtMonthlyViewCap()
        ) {
            return Promise.resolve(false);
        }

        return fetchQuery(relayEnvironment, query, { itemId, userId, buyerId: [userId] }).then(
            response => {
                const hasConversation = get(response, 'viewer.conversationsSolr.totalResults') > 0;
                const mostRecentTransaction = get(response, 'viewer.transactions.edges[0].node');
                const isActiveUser = hasRecentTransaction(mostRecentTransaction);
                if (hasConversation || isActiveUser) {
                    return false;
                }

                const currentMicroGoal = getCurrentMicroGoal();

                /*
                 *  if the current microgoal is a negotiation callout but the current item
                 *  price isn't negotiable, return false
                 */
                const isNegotiable = get(response, 'viewer.item.canBuyerMakeOffer');
                if (
                    (currentMicroGoal === SHOW_PDP_TRADE_NEGOTIATION_TOAST ||
                        currentMicroGoal === SHOW_PDP_TRADE_NEGOTIATION_MODAL) &&
                    !isNegotiable
                ) {
                    return false;
                }

                /*
                 *  if the current microgoal is the on approval callout but the current item
                 *  can't be purchased on approval, return false
                 */
                const canShowOnApproval = get(
                    response,
                    'viewer.item.displayInfo.ctas.showRequestOnApproval'
                );
                if (currentMicroGoal === SHOW_PDP_TRADE_ON_APPROVAL_CALLOUT && !canShowOnApproval) {
                    return false;
                }

                /*
                 *  if the current microgoal is the hold request callout but the current item
                 *  can't be put on hold, return false
                 */
                const canShowHold = get(response, 'viewer.item.displayInfo.ctas.showHoldRequest');
                if (currentMicroGoal === SHOW_PDP_TRADE_HOLD_REQUEST_TOAST && !canShowHold) {
                    return false;
                }

                return true;
            }
        );
    },

    action: () => dispatch => {
        dispatch({
            type: getCurrentMicroGoal(),
        });
    },
};
